import { useEffect, useMemo, useRef, useState, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import moment from "moment"
import nProgress from "nprogress"
import toast from "react-hot-toast"

import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { usePage } from "contexts/page-context"

import Table from "components/table"
import Label from "components/forms/label"
import Input from "components/forms/input"
import SelectDescription from "components/forms/select-description"
import SelectSearchable from "components/forms/select-searchable"

import { defaultUomForEntity, loadMasterUomFactors, masterUomFactor, normalizeUomCode } from "helpers/uom-helper"
import useStockTransactionRepositories from "repositories/stock-transaction"
import useBranchRepositories from "repositories/branch"
import useProductUnitRepositories from "repositories/product-unit"
import useBrandRepositories from "repositories/brand"
import useProductModelRepositories from "repositories/product-model"

import Filter from "./filter"
import AdjustmentProductPickerModal from "pages/stock-adjustment/product-picker-modal"

import { getDefaultOutletFilterValue, resolveOutletFilter, getCurrentEntity} from "helpers/general-helper"
import OutletFilterSelect from "components/forms/outlet-filter-select"

// ─── helpers ──────────────────────────────────────────────────────────────────

const createEmptyAdjustmentItem = () => ({
  key: `${Date.now()}-${Math.random()}`,
  product_unit_id: "",
  product_unit_name: "",
  physical_stock: "",
  system_stock: null,
  difference_qty: null,
  notes: "",
  is_loading_stock: false,
  stock_loaded: false,
  // Mode koreksi masuk/keluar: input = qty yang mau ditambah/dikurangi (delta),
  // stok fisik & selisih diturunkan dari system + delta.
  input_mode: "PHYSICAL",
  delta_direction: "IN",
  delta_qty: "",
})

const getRowStatus = (item, duplicatedIds = []) => {
  const id = String(item.product_unit_id || "").trim()
  const hasProduct = id !== ""
  const hasPhysical =
    item.physical_stock !== "" &&
    item.physical_stock !== null &&
    item.physical_stock !== undefined &&
    !Number.isNaN(Number(item.physical_stock))

  if (!hasProduct && !hasPhysical) return "empty"
  if (duplicatedIds.includes(id) && id) return "duplicate"
  if (!hasProduct || !hasPhysical) return "incomplete"
  if (item.system_stock === null || item.system_stock === undefined) return "unloaded"
  if (Number(item.difference_qty || 0) === 0) return "unchanged"
  return "changed"
}

const recalcRow = (item, nextPhysical = item.physical_stock, nextSystem = item.system_stock) => {
  const system =
    nextSystem === "" || nextSystem === null || nextSystem === undefined
      ? null
      : Number(nextSystem)

  if (item.input_mode === "DELTA") {
    // Mode koreksi masuk/keluar: selisih = delta yang diinput; stok fisik turunan.
    const qtyRaw = item.delta_qty
    const qty =
      qtyRaw === "" || qtyRaw === null || qtyRaw === undefined ? null : Math.abs(Number(qtyRaw))
    const signed =
      qty === null || Number.isNaN(qty) || qty === 0
        ? null
        : item.delta_direction === "OUT" ? -qty : qty
    const physical =
      signed === null || system === null || Number.isNaN(system) ? "" : String(system + signed)

    return {
      ...item,
      system_stock: nextSystem,
      physical_stock: physical,
      difference_qty: signed === null || system === null ? null : signed,
    }
  }

  const physical =
    nextPhysical === "" || nextPhysical === null || nextPhysical === undefined
      ? null
      : Number(nextPhysical)
  return {
    ...item,
    physical_stock: nextPhysical,
    system_stock: nextSystem,
    difference_qty:
      physical === null || Number.isNaN(physical) || system === null || Number.isNaN(system)
        ? null
        : physical - system,
  }
}

// ─── status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  changed:    { label: "BERUBAH",       classes: "bg-blue-50   text-blue-700   border-blue-200"   },
  unchanged:  { label: "SAMA",          classes: "bg-gray-50   text-gray-500   border-gray-200"   },
  duplicate:  { label: "DUPLIKAT",      classes: "bg-red-50    text-red-700    border-red-200"     },
  incomplete: { label: "BELUM LENGKAP", classes: "bg-amber-50  text-amber-700  border-amber-200"  },
  unloaded:   { label: "BELUM LOAD",    classes: "bg-purple-50 text-purple-700 border-purple-200" },
  empty:      { label: "KOSONG",        classes: "bg-white     text-gray-400   border-gray-200"   },
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.empty
  return (
    <span className={`inline-flex px-2.5 py-1 text-xs font-semibold border rounded-full ${cfg.classes}`}>
      {cfg.label}
    </span>
  )
}

// ─── outlet gate ──────────────────────────────────────────────────────────────

function OutletGate({ outletOptions, isLoadingOutletSelection, value, onChange }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-5">
      <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gray-100 border border-gray-200">
        <svg className="w-7 h-7 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      </div>
      <div className="text-center">
        <p className="text-base font-semibold text-gray-800">Pilih outlet untuk memulai</p>
        <p className="mt-1 text-sm text-gray-500">
          Stok sistem spesifik per outlet. Pilih outlet sebelum menambahkan item.
        </p>
      </div>
      <div className="w-full max-w-xs">
        <SelectDescription
          selection={outletOptions}
          isLoading={isLoadingOutletSelection}
          value={value}
          onChange={onChange}
          keyValue={(v) => v.id}
          title={(v) => v.name}
          description={(v) => v.code || v.id}
          placeholder="Pilih Outlet..."
        />
      </div>
    </div>
  )
}

// ─── outlet change warning banner ─────────────────────────────────────────────

function OutletChangeWarning({ pendingOutlet, pendingOutletName, onConfirm, onCancel }) {
  return (
    <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
      <svg className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <div className="flex-1">
        <p className="text-sm font-semibold text-amber-800">
          Ganti outlet ke <span className="font-bold">{pendingOutletName}</span>?
        </p>
        <p className="mt-0.5 text-sm text-amber-700">
          Semua nilai stok sistem akan diambil ulang untuk outlet baru. Entri stok fisik dan catatan Anda akan dipertahankan.
        </p>
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 text-xs font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
          >
            Ya, ganti outlet
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-xs font-semibold text-amber-700 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── submit blocker ───────────────────────────────────────────────────────────

function SubmitBlocker({ outletId, changedRows, hasInvalidRows, duplicatedIds, notes }) {
  const reasons = []
  if (!outletId)              reasons.push("Outlet belum dipilih")
  if (duplicatedIds.length)   reasons.push(`${duplicatedIds.length} produk duplikat`)
  if (hasInvalidRows)         reasons.push("Beberapa baris memiliki error (incomplete atau unloaded)")
  if (!changedRows.length && outletId) reasons.push("Tidak ada item dengan perubahan stok")
  // ATALLA-388: alasan koreksi stok wajib diisi (240726: hanya ini yg mandatory).
  if (!String(notes ?? "").trim()) reasons.push("Alasan koreksi belum diisi")

  if (!reasons.length) return null

  return (
    <div className="flex items-start gap-2 text-sm text-gray-500">
      <svg className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <span>
        Tidak dapat submit: {reasons.join(" · ")}
      </span>
    </div>
  )
}

// ─── main component ───────────────────────────────────────────────────────────

export const StockAdjustmentContent = ({
  adjustmentId = null,
  initialData = null,
  onClose,
  onSuccess,
  canSubmit = true,
}) => {
  const { configuration } = usePage()
  const { user, canFilterAllOutlets } = useAuth()

  const [outletId, setOutletId] = useState("")
  const [pendingOutletId, setPendingOutletId] = useState(null)
  const [adjustmentDate, setAdjustmentDate] = useState(moment().format("YYYY-MM-DDTHH:mm"))
  const [adjustmentNotes, setAdjustmentNotes] = useState("")
  // PHYSICAL = input stok fisik (hitungan akhir, perilaku lama);
  // DELTA    = input qty koreksi masuk/keluar (selisih yang diinginkan).
  const [inputMode, setInputMode] = useState("PHYSICAL")
  // 210726: arah koreksi (masuk/keluar) mode DELTA jadi GLOBAL — satu toggle utk
  // semua baris (dulu per-item). Qty per item cukup angka positif.
  // 210726 (rev): + opsi BOTH ("2 arah") — arah dipilih per baris produk.
  const [deltaDirection, setDeltaDirection] = useState("IN")
  // 250726 (permintaan user): satuan INPUT qty (PCS/LUSIN/PASANG) — GLOBAL semua baris.
  // Qty diketik dalam satuan ini lalu dikonversi ke PCS (× faktor) utk state & payload;
  // stok sistem + selisih ditampilkan dalam satuan sama (÷ faktor). Nilai internal & yang
  // dikirim ke backend TETAP PCS — tidak ada perubahan backend.
  // 250726 (fix): satuan input dipertahankan lintas save-draft/reload (dulu ke-reset ke
  // PCS saat form re-mount pasca simpan). Preferensi UI di localStorage — qty tetap PCS
  // di backend, satuan hanya cara tampil/input.
  const [adjUom, setAdjUom] = useState(
    () => { try { return localStorage.getItem("stockAdjUom") || "PCS" } catch { return "PCS" } }
  )
  const adjFactor = masterUomFactor(adjUom)

  // 290726 (permintaan user): opsi satuan disamakan dgn menu Pembelian — diambil dari
  // MASTER satuan (LSN/PSG/GRS/…), bukan 3 opsi hardcode. Nilai lama di localStorage
  // ("LUSIN"/"PASANG") dinormalkan ke kode master supaya cocok dgn daftar opsi.
  const ADJ_UOM_FALLBACK = ["PCS", "LUSIN", "PASANG"]
  const [adjSatuanOptions, setAdjSatuanOptions] = useState(null)
  useEffect(() => {
    let cancelled = false
    loadMasterUomFactors().then((names) => {
      if (cancelled) return
      const opts = names.length ? names : [...ADJ_UOM_FALLBACK]
      setAdjSatuanOptions(opts)
      setAdjUom((prev) => {
        const stored = normalizeUomCode(prev)
        if (stored && stored !== "PCS") return stored
        // Belum pernah memilih → ikut ENTITAS user (VISION→PSG, ATALLA→LSN).
        let saved = null
        try { saved = localStorage.getItem("stockAdjUom") } catch { /* ignore */ }
        return saved ? stored : defaultUomForEntity(getCurrentEntity(), opts)
      })
    })
    return () => { cancelled = true }
  }, [])
  // 290726 (laporan user): ANGKA TIDAK BISA DIHAPUS dgn backspace saat satuan LUSIN.
  // Sebabnya input terkontrol penuh: tiap ketikan langsung dikonversi ke PCS
  // (×faktor, dibulatkan) lalu ditampilkan balik (÷faktor, 2 desimal). Mengetik "0,3"
  // jadi 3,6 → dibulatkan 4 pcs → tampil "0,33" — angka melompat balik dan backspace
  // seolah tak berfungsi.
  //
  // Perbaikan: selama field DIKETIK, yang ditampilkan adalah teks mentah user (draft);
  // konversi ke PCS tetap jalan untuk state/payload, tapi TIDAK memaksa balik ke kotak
  // input. Draft dibuang saat blur (atau saat satuan diganti).
  const [qtyDraft, setQtyDraft] = useState({})
  const draftKey = (key, field) => `${key}|${field}`
  const setDraft = (key, field, raw) =>
    setQtyDraft((d) => ({ ...d, [draftKey(key, field)]: raw }))
  const clearDraft = (key, field) =>
    setQtyDraft((d) => {
      const next = { ...d }
      delete next[draftKey(key, field)]
      return next
    })

  // PCS <-> satuan input. State selalu PCS; input ditampilkan ÷ faktor.
  const uomToPcs = (v) => {
    if (v === "" || v === null || v === undefined) return ""
    const n = Number(v)
    return Number.isFinite(n) ? String(Math.round(n * adjFactor)) : ""
  }
  const pcsToUom = (pcs) => {
    if (pcs === "" || pcs === null || pcs === undefined) return ""
    const n = Number(pcs)
    if (!Number.isFinite(n)) return ""
    return String(Math.round((n / adjFactor) * 100) / 100)
  }
  // Tampilan stok (PCS) → satuan terpilih; "…" saat belum termuat.
  const fmtStockUom = (pcs) => {
    if (pcs === undefined || pcs === null) return "…"
    return adjFactor > 1 ? `${pcsToUom(pcs)} ${adjUom}` : pcsToUom(pcs)
  }
  const [productSearch, setProductSearch] = useState("")
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("")
  const [selectedProductId, setSelectedProductId] = useState("")  // toolbar combobox selection
  const [selectedProductRow, setSelectedProductRow] = useState(null)
  const [adjustmentItems, setAdjustmentItems] = useState([])
  // 230726 (permintaan user): product_unit_id yang ditolak backend saat submit
  // (mis. stok keburu berkurang) — barisnya disorot merah supaya gampang dicari.
  const [problemIds, setProblemIds] = useState([])
  // 240726 (#5): sorting produk (name) asc/desc, disimpan di localStorage supaya
  // pilihan bertahan antar sesi. "" = urutan input (default), "asc"/"desc" = abjad.
  const [productSort, setProductSort] = useState(
    () => { try { return localStorage.getItem("stockAdjProductSort") || "" } catch { return "" } }
  )
  const cycleProductSort = () => {
    const next = productSort === "asc" ? "desc" : productSort === "desc" ? "" : "asc"
    setProductSort(next)
    try { next ? localStorage.setItem("stockAdjProductSort", next) : localStorage.removeItem("stockAdjProductSort") } catch { /* ignore */ }
  }

  // 240726 (#1): Enter di qty koreksi → pindah fokus ke input qty baris berikutnya.
  // Berbasis DOM: kumpulkan semua input qty (id qty-adj-input-*) urut dokumen, cari
  // posisi input yang SEDANG fokus, fokuskan yang berikutnya. Robust thd sort/gap
  // (tak bergantung index numerik). Baris terakhir → no-op.
  const focusNextQtyInput = () => {
    const all = Array.from(document.querySelectorAll('input[id^="qty-adj-input-"]'))
    const pos = all.indexOf(document.activeElement)
    const next = pos >= 0 ? all[pos + 1] : null
    if (next) {
      next.focus()
      if (typeof next.select === "function") next.select()
    }
  }
  // Baris terurut utk TAMPILAN saja (update tetap by-key, jadi aman disort).
  const displayAdjustmentItems = useMemo(() => {
    if (!productSort) return adjustmentItems
    const dir = productSort === "desc" ? -1 : 1
    return [...adjustmentItems].sort(
      (a, b) => dir * String(a.product_unit_name || "").localeCompare(
        String(b.product_unit_name || ""), "id", { sensitivity: "base", numeric: true }
      )
    )
  }, [adjustmentItems, productSort])
  const [loadingSystemStock, setLoadingSystemStock] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // 200726: picker multi-select — tambah banyak product unit sekaligus (mirror form PO).
  const [showBulkPicker, setShowBulkPicker] = useState(false)
  const currentAdjustmentStatus = String(initialData?.adjustment?.status || "").toUpperCase()

  const { data: outletSelection, isLoading: isLoadingOutletSelection } =
    useBranchRepositories({ paginate: false, stockScope: true })

  // 250726 (permintaan user): opsi outlet diurutkan ascending by id (seragam dgn
  // filter outlet lain).
  const outletOptions = useMemo(() => {
    const rows = Array.isArray(outletSelection) ? outletSelection : (outletSelection || [])
    return rows.slice().sort((a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0))
  }, [outletSelection])

  const selectedOutlet = useMemo(
    () => outletOptions.find((row) => String(row.id) === String(outletId)),
    [outletId, outletOptions]
  )

  const pendingOutletName = useMemo(() => {
    const found = outletOptions.find((row) => String(row.id) === String(pendingOutletId))
    return found?.name || pendingOutletId
  }, [pendingOutletId, outletOptions])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedProductSearch(productSearch.trim())
    }, 400)

    return () => clearTimeout(timeoutId)
  }, [productSearch])

  const {
    data: productUnitSelectionRaw,
    isLoading: isLoadingProductUnitSelection,
  } = useProductUnitRepositories({
    paginate: true,
    search: debouncedProductSearch || undefined,
  })

  const productUnitOptions = useMemo(() => {
    if (Array.isArray(productUnitSelectionRaw)) return productUnitSelectionRaw
    if (Array.isArray(productUnitSelectionRaw?.data)) return productUnitSelectionRaw.data
    return []
  }, [productUnitSelectionRaw])

  // Permintaan user 170726 (#2): tampilkan STOK produk (outlet terpilih) di dropdown
  // picker. Batch 1 panggilan /stock_list per perubahan daftar opsi — bukan N+1.
  const [optionStocks, setOptionStocks] = useState({})
  useEffect(() => {
    const ids = (productUnitOptions || []).map((v) => String(v.id)).filter(Boolean)
    if (!outletId || ids.length === 0) { setOptionStocks({}); return }
    let cancelled = false
    fetch(`${process.env.REACT_APP_INVENTORY_API_URL}/stock_list`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ product_unit_ids: ids, outlet_id: Number(outletId) }),
    })
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return
        const map = {}
        ;(Array.isArray(json?.data) ? json.data : []).forEach((row) => {
          const id = String(row?.product_unit_id ?? "")
          if (id) map[id] = Number(row?.current_stock ?? row?.stock ?? row?.balance ?? 0)
        })
        setOptionStocks(map)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [productUnitOptions, outletId])

  // Set default outlet from config
  useEffect(() => {
    const defaultOutletId =
      configuration?.outlet_id ||
      configuration?.user?.outlet_id ||
      ""
    if (defaultOutletId) setOutletId(String(defaultOutletId))
  }, [configuration])

  // Ganti mode input → sinkronkan semua baris & reset input qty-nya (fisik vs delta
  // maknanya beda, tidak boleh terbawa).
  useEffect(() => {
    setAdjustmentItems((prev) =>
      prev.map((item) =>
        recalcRow({
          ...item,
          input_mode: inputMode,
          ...(inputMode === "PHYSICAL"
            ? { delta_qty: "", delta_direction: "IN" }
            : {
                physical_stock: "",
                // BOTH: arah per baris — pertahankan pilihan baris (default IN).
                delta_direction:
                  deltaDirection === "BOTH" ? item.delta_direction || "IN" : deltaDirection,
              }),
        })
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMode])

  // Arah koreksi global (masuk/keluar) berubah → terapkan ke SEMUA baris DELTA.
  // BOTH: arah dipilih per baris — jangan timpa pilihan user.
  useEffect(() => {
    if (inputMode !== "DELTA" || deltaDirection === "BOTH") return
    setAdjustmentItems((prev) =>
      prev.map((item) =>
        item.input_mode === "DELTA"
          ? recalcRow({ ...item, delta_direction: deltaDirection })
          : item
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deltaDirection])

  useEffect(() => {
    if (!initialData?.adjustment) return

    const adjustment = initialData.adjustment
    const items = Array.isArray(initialData?.items) ? initialData.items : []

    setOutletId(String(adjustment.outlet_id || ""))
    setPendingOutletId(null)
    setAdjustmentDate(
      adjustment.adjustment_date
        ? moment(adjustment.adjustment_date).format("YYYY-MM-DDTHH:mm")
        : moment().format("YYYY-MM-DDTHH:mm")
    )
    setAdjustmentNotes(String(adjustment.notes || ""))
    setSelectedProductId("")
    setSelectedProductRow(null)
    setProductSearch("")
    setDebouncedProductSearch("")

    // 210726: pulihkan MODE input dari meta draft. Item mode koreksi masuk/keluar
    // menyimpan meta.input_mode='DELTA' + meta.delta_qty (bertanda). Tanpa hidrasi
    // ini, draft mode delta dibuka ulang selalu jatuh ke mode stok fisik (bug: input
    // "gk nyesuain, default ke stok fisik").
    const draftIsDelta = items.some(
      (it) => String(it?.meta?.input_mode || "").toUpperCase() === "DELTA"
    )
    setInputMode(draftIsDelta ? "DELTA" : "PHYSICAL")

    // Arah global diambil dari tanda delta yang tidak nol. Campuran +/-
    // (draft dibuat dgn arah "2 arah" per baris) → global BOTH.
    if (draftIsDelta) {
      const signs = items
        .map((it) => Number(it?.meta?.delta_qty ?? 0))
        .filter((n) => n !== 0)
      const hasIn = signs.some((n) => n > 0)
      const hasOut = signs.some((n) => n < 0)
      setDeltaDirection(hasIn && hasOut ? "BOTH" : hasOut ? "OUT" : "IN")
    } else {
      setDeltaDirection("IN")
    }

    setAdjustmentItems(
      items.map((item) => {
        const meta = item?.meta || {}
        const isDelta = String(meta.input_mode || "").toUpperCase() === "DELTA"
        // meta.delta_qty tersimpan BERTANDA (mis. -5 keluar, +10 masuk).
        const signedDelta = Number(meta.delta_qty ?? item.difference_qty ?? 0)

        const base = {
          ...createEmptyAdjustmentItem(),
          key: `${item.id || item.reference_id || item.product_unit_id}-${Math.random()}`,
          product_unit_id: String(item.product_unit_id || ""),
          product_unit_name: item.product_unit_name || "",
          system_stock: Number(item.system_stock ?? 0),
          difference_qty: Number(item.difference_qty ?? 0),
          notes: item.notes || "",
          stock_loaded: true,
          is_loading_stock: false,
          input_mode: isDelta ? "DELTA" : "PHYSICAL",
          ...(isDelta
            ? {
                delta_direction: signedDelta < 0 ? "OUT" : "IN",
                delta_qty: signedDelta === 0 ? "" : String(Math.abs(signedDelta)),
                physical_stock: "",
              }
            : { physical_stock: String(item.physical_stock ?? "") }),
        }

        // Turunkan physical_stock & difference_qty konsisten dgn mode-nya.
        return recalcRow(base)
      })
    )
  }, [initialData])

  // ── derived state ────────────────────────────────────────────────────────

  const selectedProductMap = useMemo(() => {
    const map = {}
    for (const item of adjustmentItems) {
      const id = String(item.product_unit_id || "").trim()
      if (!id) continue
      map[id] = (map[id] || 0) + 1
    }
    return map
  }, [adjustmentItems])

  const duplicatedIds = useMemo(
    () => Object.entries(selectedProductMap).filter(([, count]) => count > 1).map(([id]) => id),
    [selectedProductMap]
  )

  const hasFilledItems = useMemo(
    () => adjustmentItems.some((item) =>
      String(item.product_unit_id || "").trim() !== "" || item.physical_stock !== ""
    ),
    [adjustmentItems]
  )

  const validRows = useMemo(
    () => adjustmentItems.filter((item) => ["changed", "unchanged"].includes(getRowStatus(item, duplicatedIds))),
    [adjustmentItems, duplicatedIds]
  )

  const changedRows = useMemo(
    () => adjustmentItems.filter((item) => getRowStatus(item, duplicatedIds) === "changed"),
    [adjustmentItems, duplicatedIds]
  )

  const hasInvalidRows = useMemo(
    () => adjustmentItems.some((item) =>
      ["duplicate", "incomplete", "unloaded"].includes(getRowStatus(item, duplicatedIds))
    ),
    [adjustmentItems, duplicatedIds]
  )

  // 240726 (#2 permintaan user): DRAFT boleh menyimpan baris yang qty koreksinya
  // masih 0 — asalkan produk sudah dipilih & stok sistem sudah termuat. Baris tak
  // ter-koreksi disimpan sebagai no-op (physical=system) supaya tetap muncul saat
  // draft dibuka lagi (tak boleh hilang). Duplikat & unloaded tetap dikecualikan.
  const draftSavableRows = useMemo(
    () => adjustmentItems.filter((item) => {
      const id = String(item.product_unit_id || "").trim()
      if (!id || duplicatedIds.includes(id)) return false
      const systemLoaded = item.system_stock !== null && item.system_stock !== undefined && item.system_stock !== ""
      return systemLoaded
    }),
    [adjustmentItems, duplicatedIds]
  )
  // 150726 (ATALLA-394): catatan PER PRODUK wajib diisi. Baris yang akan disimpan
  // (changed saat posting, changed+unchanged saat draft) harus punya catatan non-kosong.
  // 240726 (#2): catatan per produk tidak lagi wajib — memo cek missing dihapus.
  const canSubmitForm = useMemo(
    // ATALLA-388: alasan (catatan) koreksi stok wajib diisi sebelum submit.
    // 240726 (#2 permintaan user): catatan PER PRODUK TIDAK lagi wajib — hanya
    // catatan koreksi (header) yang mandatory (syarat changedRowsMissingNotes dilepas).
    () => canSubmit && !!outletId && changedRows.length > 0 && !hasInvalidRows && !submitting && !loadingSystemStock && !!adjustmentNotes.trim(),
    [canSubmit, outletId, changedRows.length, hasInvalidRows, submitting, loadingSystemStock, adjustmentNotes]
  )
  const canSaveDraft = useMemo(
    // 240726 (#2): draft cukup punya produk + stok termuat (qty 0 boleh); catatan
    // per produk tidak wajib — hanya catatan koreksi (header).
    () => canSubmit && !!outletId && draftSavableRows.length > 0 && !duplicatedIds.length && !submitting && !loadingSystemStock && !!adjustmentNotes.trim(),
    [canSubmit, outletId, draftSavableRows.length, duplicatedIds.length, submitting, loadingSystemStock, adjustmentNotes]
  )

  const summary = useMemo(() => {
    const totalIncrease = changedRows.reduce((sum, item) => {
      const diff = Number(item.difference_qty)
      return diff > 0 ? sum + diff : sum
    }, 0)
    const totalDecrease = changedRows.reduce((sum, item) => {
      const diff = Number(item.difference_qty)
      return diff < 0 ? sum + Math.abs(diff) : sum
    }, 0)
    return { totalItems: validRows.length, changedItems: changedRows.length, totalIncrease, totalDecrease }
  }, [validRows, changedRows])

  // ── stock loading ────────────────────────────────────────────────────────

  const loadStockForRow = useCallback(async (productUnitId, currentOutletId) => {
    // console.log("Loading stock for product_unit_id", productUnitId, "and outlet_id", currentOutletId)
    if (!productUnitId || !currentOutletId) return null
    const response = await fetch(`${process.env.REACT_APP_INVENTORY_API_URL}/stock_list`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ product_unit_ids: [String(productUnitId)], outlet_id: Number(currentOutletId) }),
    })
    const json = await response.json()
    if (!response.ok || Number(json?.success) !== 1)
      throw new Error(json?.message || "Gagal mengambil stok sistem")
    const rows = Array.isArray(json?.data) ? json.data : []
    const found = rows[0]
    return Number(found?.current_stock ?? found?.stock ?? found?.balance ?? 0)
  }, [])

  const reloadStockForRow = async (key) => {
    const item = adjustmentItems.find((i) => i.key === key)
    if (!item || !item.product_unit_id || !outletId) return

    setAdjustmentItems((prev) =>
      prev.map((i) => i.key === key ? { ...i, is_loading_stock: true } : i)
    )
    try {
      const systemStock = await loadStockForRow(item.product_unit_id, outletId)
      setAdjustmentItems((prev) =>
        prev.map((i) =>
          i.key === key
            ? { ...recalcRow(i, i.physical_stock, systemStock), is_loading_stock: false, stock_loaded: true }
            : i
        )
      )
    } catch (error) {
      setAdjustmentItems((prev) =>
        prev.map((i) => i.key === key ? { ...i, is_loading_stock: false } : i)
      )
      toast.error(error?.message || "Gagal mengambil stok sistem")
    }
  }

  const loadAllStocks = async (targetOutletId = outletId) => {
    const productIds = adjustmentItems
      .map((item) => String(item.product_unit_id || "").trim())
      .filter(Boolean)

    if (!targetOutletId || !productIds.length) return

    try {
      setLoadingSystemStock(true)
      setAdjustmentItems((prev) =>
        prev.map((item) =>
          String(item.product_unit_id || "").trim()
            ? { ...item, is_loading_stock: true }
            : item
        )
      )
      const response = await fetch(`${process.env.REACT_APP_INVENTORY_API_URL}/stock_list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          "X-Request-Id": `${Date.now()}-${Math.random()}`,
        },
        body: JSON.stringify({ product_unit_ids: productIds, outlet_id: Number(targetOutletId) }),
      })
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1)
        throw new Error(json?.message || "Gagal mengambil stok sistem")

      const rows = Array.isArray(json?.data) ? json.data : []
      const byId = rows.reduce((acc, row) => {
        const id = String(row?.product_unit_id ?? row?.product_unit?.id ?? "")
        if (!id) return acc
        acc[id] = row
        return acc
      }, {})

      setAdjustmentItems((prev) =>
        prev.map((item) => {
          const itemId = String(item.product_unit_id || "")
          const found = byId[itemId]
          const systemStock = Number(found?.current_stock ?? found?.stock ?? found?.balance ?? 0)
          return { ...recalcRow(item, item.physical_stock, systemStock), stock_loaded: !!itemId, is_loading_stock: false }
        })
      )
      return true
    } catch (error) {
      toast.error(error?.message || "Gagal memuat stok sistem")
      return false
    } finally {
      setLoadingSystemStock(false)
    }
  }

  // ── item mutation ────────────────────────────────────────────────────────

  const updateAdjustmentItem = (key, field, value) => {
    if (field === "physical_stock") {
      setAdjustmentItems((prev) =>
        prev.map((item) => item.key !== key ? item : recalcRow(item, value, item.system_stock))
      )
      return
    }
    if (field === "delta_qty" || field === "delta_direction") {
      setAdjustmentItems((prev) =>
        prev.map((item) => item.key !== key ? item : recalcRow({ ...item, [field]: value }))
      )
      return
    }
    setAdjustmentItems((prev) =>
      prev.map((item) => item.key !== key ? item : { ...item, [field]: value })
    )
  }

  const addAdjustmentItem = async () => {
    if (!selectedProductId) {
      toast.error("Pilih product unit terlebih dahulu")
      return
    }
    const alreadyAdded = adjustmentItems.some(
      (item) => String(item.product_unit_id) === String(selectedProductId)
    )
    if (alreadyAdded) {
      toast.error("Product ini sudah ada di daftar adjustment")
      return
    }
    const selected =
      selectedProductRow ||
      productUnitOptions.find((row) => String(row.id) === String(selectedProductId))
    const newItem = {
      ...createEmptyAdjustmentItem(),
      input_mode: inputMode,
      delta_direction: deltaDirection === "BOTH" ? "IN" : deltaDirection,
      product_unit_id: selectedProductId,
      product_unit_name: selected?.long_name || selected?.name || "",
      product_unit_sku: [selected?.sku, selected?.product_model?.name].filter(Boolean).join(" · "),
      is_loading_stock: !!outletId,
    }
    setAdjustmentItems((prev) => [newItem, ...prev])
    setSelectedProductId("")
    setSelectedProductRow(null)
    setProductSearch("")
    setDebouncedProductSearch("")
    if (outletId) {
      try {
        const systemStock = await loadStockForRow(selectedProductId, outletId)
        setAdjustmentItems((prev) =>
          prev.map((item) =>
            item.product_unit_id === selectedProductId && !item.stock_loaded
              ? { ...recalcRow(item, item.physical_stock, systemStock), is_loading_stock: false, stock_loaded: true }
              : item
          )
        )
      } catch (error) {
        setAdjustmentItems((prev) =>
          prev.map((item) =>
            item.product_unit_id === selectedProductId && item.is_loading_stock
              ? { ...item, is_loading_stock: false }
              : item
          )
        )
        toast.error(error?.message || "Gagal mengambil stok sistem")
      }
    }
  }

  // 200726: tambah banyak produk sekaligus dari picker modal. Duplikat (sudah ada di
  // daftar) di-skip; stok sistem semua baris baru dimuat dgn SATU panggilan batch.
  const addAdjustmentItemsBulk = async (products) => {
    const fresh = (products || []).filter(
      (p) => !adjustmentItems.some((item) => String(item.product_unit_id) === String(p?.id))
    )
    setShowBulkPicker(false)

    if (!fresh.length) {
      toast.error("Semua produk yang dipilih sudah ada di daftar")
      return
    }

    const newItems = fresh.map((p) => ({
      ...createEmptyAdjustmentItem(),
      input_mode: inputMode,
      delta_direction: deltaDirection === "BOTH" ? "IN" : deltaDirection,
      product_unit_id: String(p.id),
      product_unit_name: p?.long_name || p?.name || "",
      product_unit_sku: [p?.sku, p?.product_model?.name].filter(Boolean).join(" · "),
      is_loading_stock: !!outletId,
    }))
    setAdjustmentItems((prev) => [...newItems, ...prev])
    toast.success(`${newItems.length} produk ditambahkan`)

    if (!outletId) return

    const ids = fresh.map((p) => String(p.id))
    try {
      const response = await fetch(`${process.env.REACT_APP_INVENTORY_API_URL}/stock_list`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ product_unit_ids: ids, outlet_id: Number(outletId) }),
      })
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1)
        throw new Error(json?.message || "Gagal mengambil stok sistem")

      const byId = (Array.isArray(json?.data) ? json.data : []).reduce((acc, row) => {
        const id = String(row?.product_unit_id ?? "")
        if (id) acc[id] = Number(row?.current_stock ?? row?.stock ?? row?.balance ?? 0)
        return acc
      }, {})

      setAdjustmentItems((prev) =>
        prev.map((item) =>
          ids.includes(String(item.product_unit_id)) && !item.stock_loaded
            ? {
                ...recalcRow(item, item.physical_stock, byId[String(item.product_unit_id)] ?? 0),
                is_loading_stock: false,
                stock_loaded: true,
              }
            : item
        )
      )
    } catch (error) {
      setAdjustmentItems((prev) =>
        prev.map((item) =>
          ids.includes(String(item.product_unit_id)) && item.is_loading_stock
            ? { ...item, is_loading_stock: false }
            : item
        )
      )
      toast.error(error?.message || "Gagal mengambil stok sistem")
    }
  }

  const removeAdjustmentItem = (key) =>
    setAdjustmentItems((prev) => prev.filter((item) => item.key !== key))

  // ── outlet change (inline confirmation) ─────────────────────────────────

  const handleChangeOutlet = (value) => {
    if (hasFilledItems && value !== outletId) {
      setPendingOutletId(value)
      return
    }
    applyOutletChange(value)
  }

  const applyOutletChange = async (value) => {
    setOutletId(value)
    setPendingOutletId(null)
    setSelectedProductId("")
    setSelectedProductRow(null)
    setProductSearch("")
    setDebouncedProductSearch("")
    setAdjustmentItems((prev) =>
      prev.map((item) => ({
        ...item,
        system_stock: null,
        difference_qty: null,
        stock_loaded: false,
        is_loading_stock: false,
      }))
    )
    const validProductIds = adjustmentItems
      .map((item) => String(item.product_unit_id || "").trim())
      .filter(Boolean)
    if (value && validProductIds.length) {
      const ok = await loadAllStocks(value)
      if (ok) toast.success("Stok sistem diperbarui untuk outlet baru")
    }
  }

  const cancelOutletChange = () => setPendingOutletId(null)

  // ── submit ───────────────────────────────────────────────────────────────

  const buildPayload = (items, status) => ({
    status,
    // ATALLA-343: kirim nama user sbg actor audit (raw fetch tak kirim Bearer token,
    // jadi ScopeOutletByEntity tak bisa resolve user → created_by jatuh ke "SYSTEM").
    created_by: user?.name || undefined,
    adjustment_date: moment(adjustmentDate).format("YYYY-MM-DD HH:mm:ss"),
    outlet_id: Number(outletId),
    outlet: selectedOutlet?.name || null,
    outlet_group_id: selectedOutlet?.outlet_group_id || selectedOutlet?.branch_group_id || null,
    outlet_group: selectedOutlet?.outlet_group?.name || selectedOutlet?.branch_group?.name || selectedOutlet?.outlet_group_name || null,
    notes: adjustmentNotes || null,
    items: items.map((item) => {
      const system = Number(item.system_stock)
      const diff = Number(item.difference_qty)
      const hasDelta = item.input_mode === "DELTA" && Number.isFinite(diff) && diff !== 0
      const physFilled = item.physical_stock !== "" && item.physical_stock !== null && item.physical_stock !== undefined
      // 240726 (#2): baris TANPA koreksi (delta 0 / fisik kosong) disimpan no-op
      // (physical = system) — backend menolak delta_qty=0, jadi tak bisa kirim delta 0.
      let qtyPayload
      if (hasDelta) {
        // Mode delta: kirim qty koreksi (selisih bertanda) — backend hitung stok fisik.
        qtyPayload = { delta_qty: diff }
      } else if (item.input_mode !== "DELTA" && physFilled) {
        qtyPayload = { physical_stock: Number(item.physical_stock) }
      } else {
        qtyPayload = { physical_stock: Number.isFinite(system) ? system : 0 }
      }
      return {
        product_unit_id: String(item.product_unit_id || "").trim(),
        ...qtyPayload,
        notes: item.notes || "",
      }
    }),
  })

  // 230726 (permintaan user): pesan validasi backend ada di json.errors —
  // json.message cuma "Invalid provided data." (pola ATALLA-345). Kasus nyata:
  // stok barang BERKURANG (mis. terjual di POS) saat admin sedang mengisi
  // koreksi; submit ditolak backend dgn pesan spesifik "Koreksi keluar ...
  // melebihi stok sistem (N)" yang dulu tak pernah sampai ke user.
  const firstApiError = (json, fallback) => {
    const flat = json?.errors && typeof json.errors === "object"
      ? Object.values(json.errors).flat().filter(Boolean)
      : []
    return flat.length > 0 ? String(flat[0]) : (json?.message || fallback)
  }

  // 230726: backend menandai baris bermasalah via key errors "items.<product_unit_id>"
  // — dipetakan balik utk menyorot baris di tabel (key index Laravel "items.0.x"
  // tak akan cocok dgn product_unit_id, jadi aman ikut terambil).
  const extractProblemIds = (json) => Object.keys(json?.errors || {})
    .filter((k) => k.startsWith("items."))
    .map((k) => k.slice("items.".length))
    .filter(Boolean)

  const handleSaveDraft = async () => {
    if (!outletId) { toast.error("Outlet wajib dipilih"); return }
    if (duplicatedIds.length) { toast.error("Product unit tidak boleh duplikat"); return }
    // 240726 (#2): draft simpan SEMUA baris ber-produk (termasuk qty 0) — tak boleh
    // ada item yang hilang dari draft hanya karena belum dikoreksi.
    if (!draftSavableRows.length) { toast.error("Tambahkan minimal 1 produk untuk draft"); return }
    // 150726 (ATALLA-394): catatan koreksi stok wajib diisi sebelum simpan draft.
    if (!adjustmentNotes.trim()) { toast.error("Alasan / catatan koreksi stok wajib diisi"); return }
    // 240726 (#2): catatan per produk TIDAK lagi wajib.

    const timestamp = new Date().toISOString()
    toast.loading("Menyimpan draft koreksi stok...", { id: `toast-draft-${timestamp}` })

    try {
      setSubmitting(true)
      const payload = buildPayload(draftSavableRows, "DRAFT")
      const url = adjustmentId
        ? `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments/${adjustmentId}`
        : `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments`
      const method = adjustmentId ? "PUT" : "POST"

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      })
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1) {
        setProblemIds(extractProblemIds(json))
        throw new Error(firstApiError(json, "Gagal menyimpan draft koreksi stok"))
      }

      setProblemIds([])
      toast.success(json?.message || "Draft koreksi stok berhasil disimpan", { id: `toast-draft-${timestamp}` })
      onSuccess?.(json?.data)
    } catch (error) {
      toast.error(error?.message || "Gagal menyimpan draft koreksi stok", { id: `toast-draft-${timestamp}` })
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!outletId) { toast.error("Outlet wajib dipilih"); return }
    if (duplicatedIds.length) { toast.error("Product unit tidak boleh duplikat"); return }
    if (!changedRows.length) { toast.error("Tidak ada item yang berubah"); return }
    if (hasInvalidRows) { toast.error("Masih ada item yang belum valid"); return }
    // 150726 (ATALLA-394): catatan koreksi stok wajib diisi sebelum submit.
    if (!adjustmentNotes.trim()) { toast.error("Alasan / catatan koreksi stok wajib diisi"); return }
    // 240726 (#2): catatan per produk TIDAK lagi wajib.

    const timestamp = new Date().toISOString()
    toast.loading("Memproses koreksi stok...", { id: `toast-${timestamp}` })

    try {
      setSubmitting(true)
      const payload = buildPayload(changedRows, "POSTED")
      const url = adjustmentId
        ? `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments/${adjustmentId}`
        : `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments`
      const method = adjustmentId ? "PUT" : "POST"

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      })
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1) {
        setProblemIds(extractProblemIds(json))
        throw new Error(firstApiError(json, "Gagal memproses koreksi stok"))
      }

      setProblemIds([])
      toast.success(json?.message || "Koreksi stok berhasil diproses", { id: `toast-${timestamp}` })
      onSuccess?.(json?.data)
      onClose?.()
    } catch (error) {
      toast.error(error?.message || "Gagal memproses koreksi stok", { id: `toast-${timestamp}` })
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancelDraft = async () => {
    if (!adjustmentId || currentAdjustmentStatus !== "DRAFT") return

    const confirmed = window.confirm("Batalkan draft stock adjustment ini?")
    if (!confirmed) return

    const toastId = `toast-cancel-draft-${Date.now()}`
    toast.loading("Membatalkan draft stock adjustment...", { id: toastId })

    try {
      setSubmitting(true)
      const response = await fetch(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments/${adjustmentId}/cancel`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
        }
      )

      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1) {
        throw new Error(json?.message || "Gagal membatalkan draft stock adjustment")
      }

      toast.success(json?.message || "Draft stock adjustment berhasil dibatalkan", { id: toastId })
      onSuccess?.(json?.data)
      onClose?.()
    } catch (error) {
      toast.error(error?.message || "Gagal membatalkan draft stock adjustment", { id: toastId })
    } finally {
      setSubmitting(false)
    }
  }

  const handleManualRefreshAll = async () => {
    if (!outletId) { toast.error("Silakan pilih outlet terlebih dahulu"); return }
    const productIds = adjustmentItems.map((item) => String(item.product_unit_id || "").trim()).filter(Boolean)
    if (!productIds.length) { toast.error("Pilih minimal 1 product unit terlebih dahulu"); return }
    if (duplicatedIds.length) { toast.error("Terdapat product unit duplikat"); return }
    const ok = await loadAllStocks()
    if (ok) toast.success("Stok sistem berhasil dimuat")
  }

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
          e.preventDefault()
        }
      }}
      className="flex flex-col w-full gap-5 mt-2"
    >

      {/* ── Step 1: header info ── */}
      <div className="p-5 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-gray-800 rounded-full">1</span>
          <span className="text-sm font-semibold text-gray-700">Detail Adjustment</span>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <Label value="Outlet" className="mb-1.5 block text-sm font-medium text-gray-700" />
            <SelectDescription
              selection={outletOptions}
              isLoading={isLoadingOutletSelection}
              value={outletId}
              onChange={handleChangeOutlet}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={(v) => v.code || v.id}
              placeholder="Pilih Outlet..."
            />
          </div>
          <div>
            <Label value="Tanggal Adjustment" className="mb-1.5 block text-sm font-medium text-gray-700" />
            <Input
              type="datetime-local"
              value={adjustmentDate}
              onChange={(e) => setAdjustmentDate(e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <Label value="Mode Input" className="mb-1.5 block text-sm font-medium text-gray-700" />
            <select
              value={inputMode}
              onChange={(e) => setInputMode(e.target.value)}
              className="block w-full px-4 py-2 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            >
              <option value="PHYSICAL">Stok Fisik (hitungan akhir)</option>
              <option value="DELTA">Koreksi Masuk / Keluar (qty)</option>
            </select>
            <p className="mt-1 text-[11px] text-neutral-500">
              {inputMode === "DELTA"
                ? "Input qty yang mau ditambah/dikurangi — stok fisik dihitung otomatis."
                : "Input hasil hitungan fisik — selisih dihitung otomatis."}
            </p>
          </div>
          <div>
            <Label value="Satuan Input" className="mb-1.5 block text-sm font-medium text-gray-700" />
            <select
              value={adjUom}
              onChange={(e) => {
                const v = e.target.value
                setAdjUom(v)
                // draft ketikan dibuang: angkanya milik satuan lama.
                setQtyDraft({})
                try { localStorage.setItem("stockAdjUom", v) } catch { /* ignore */ }
              }}
              className="block w-full px-4 py-2 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            >
              {((adjSatuanOptions ?? ADJ_UOM_FALLBACK).includes(adjUom)
                ? (adjSatuanOptions ?? ADJ_UOM_FALLBACK)
                : [adjUom, ...(adjSatuanOptions ?? ADJ_UOM_FALLBACK)]
              ).map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-neutral-500">
              {adjFactor > 1
                ? `Qty diketik dalam ${adjUom} (1 ${adjUom} = ${adjFactor} pcs); stok & selisih ikut ${adjUom}.`
                : "Qty diketik dalam satuan PCS."}
            </p>
          </div>
          {inputMode === "DELTA" && (
            <div>
              <Label value="Arah Koreksi (semua item)" className="mb-1.5 block text-sm font-medium text-gray-700" />
              <select
                value={deltaDirection}
                onChange={(e) => setDeltaDirection(e.target.value)}
                className="block w-full px-4 py-2 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              >
                <option value="IN">Masuk (tambah stok)</option>
                <option value="OUT">Keluar (kurangi stok)</option>
                <option value="BOTH">2 Arah (pilih per produk)</option>
              </select>
              <p className="mt-1 text-[11px] text-neutral-500">
                {deltaDirection === "BOTH"
                  ? "Arah masuk/keluar dipilih di tiap baris produk."
                  : "Berlaku untuk semua baris. Qty per item cukup angka positif."}
              </p>
            </div>
          )}
          <div className="md:col-span-2">
            <Label value="Alasan / Catatan *" className="mb-1.5 block text-sm font-medium text-gray-700" />
            <Input
              type="text"
              value={adjustmentNotes}
              onChange={(e) => setAdjustmentNotes(e.target.value)}
              placeholder="Wajib — mis. Stock opname gudang utama"
              className="w-full"
            />
          </div>
        </div>

        {/* Inline outlet change warning */}
        {pendingOutletId && (
          <div className="mt-4">
            <OutletChangeWarning
              pendingOutlet={pendingOutletId}
              pendingOutletName={pendingOutletName}
              onConfirm={() => applyOutletChange(pendingOutletId)}
              onCancel={cancelOutletChange}
            />
          </div>
        )}
      </div>

      {/* ── Step 2: items — gated on outlet ── */}
      {!outletId ? (
        <div className="bg-white border border-gray-200 shadow-sm rounded-2xl">
          <div className="flex items-center gap-2 px-5 pt-5 mb-2">
            <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-gray-400 border-2 border-gray-200 rounded-full">2</span>
            <span className="text-sm font-semibold text-gray-400">Item Adjustment</span>
          </div>
          <OutletGate
            outletOptions={outletOptions}
            isLoadingOutletSelection={isLoadingOutletSelection}
            value={outletId}
            onChange={handleChangeOutlet}
          />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              // Item Valid & Item Berubah = JUMLAH item (count) — tidak ikut satuan.
              { label: "Item Valid",       value: summary.totalItems,    cls: "text-gray-900",  border: "border-gray-200",  bg: "bg-white" },
              { label: "Item Berubah",     value: summary.changedItems,  cls: "text-blue-700",  border: "border-blue-200",  bg: "bg-white" },
              // Total penambahan/pengurangan = QTY → ikut satuan input (÷ faktor).
              { label: `Total Penambahan${adjFactor > 1 ? ` (${adjUom})` : ""}`, value: `+${pcsToUom(summary.totalIncrease)}`, cls: "text-green-700", border: "border-green-200", bg: "bg-green-50/40" },
              { label: `Total Pengurangan${adjFactor > 1 ? ` (${adjUom})` : ""}`, value: `-${pcsToUom(summary.totalDecrease)}`, cls: "text-red-700",   border: "border-red-200",   bg: "bg-red-50/40"   },
            ].map(({ label, value, cls, border, bg }) => (
              <div key={label} className={`flex flex-col justify-center p-4 border ${border} ${bg} shadow-sm rounded-2xl`}>
                <div className={`text-xs font-semibold uppercase tracking-wide ${cls} opacity-70`}>{label}</div>
                <div className={`mt-1 text-3xl font-bold tracking-tight ${cls}`}>{value}</div>
              </div>
            ))}
          </div>

          {/* Items table */}
          <div className="relative z-20 flex flex-col bg-white border border-gray-200 shadow-sm rounded-2xl">
            {/* Table toolbar */}
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-gray-800 rounded-full">2</span>
                <span className="text-sm font-semibold text-gray-700">Item Adjustment</span>
                <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Hanya baris <strong>CHANGED</strong> yang disubmit
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-5 md:items-end">
                <div className="relative z-30 md:col-span-2">
                  <Label value="Cari & Pilih Produk" className="mb-1.5 block text-xs font-medium text-gray-500 uppercase tracking-wide" />
                  <SelectSearchable
                    selection={productUnitOptions}
                    isLoading={isLoadingProductUnitSelection}
                    value={selectedProductId}
                    onChange={(value, row) => {
                      const nextId = String(value || "")
                      setSelectedProductId(nextId)
                      setSelectedProductRow(row || null)
                    }}
                    keyValue={(v) => v.id}
                    title={(v) => v.long_name || v.name}
                    description={(v) => [
                      v.sku,
                      v.product_model?.name,
                      outletId ? `Stok: ${fmtStockUom(optionStocks[String(v.id)])}` : null,
                    ].filter(Boolean).join(" · ")}
                    placeholder="Cari product unit / SKU..."
                    onSearchChange={(q) => setProductSearch(String(q || ""))}
                    serverSearch
                    clearOnClose={false}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleManualRefreshAll}
                  disabled={loadingSystemStock || submitting}
                  className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 transition-all bg-white border border-gray-300 shadow-sm rounded-xl hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 disabled:opacity-60 disabled:hover:bg-white"
                >
                  {loadingSystemStock ? "Memuat..." : "Refresh Semua Stok"}
                </button>
                <button
                  type="button"
                  onClick={addAdjustmentItem}
                  disabled={submitting || !selectedProductId}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white transition-all bg-gray-900 shadow-sm rounded-xl hover:bg-gray-800 focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  + Tambah Item
                </button>
                <button
                  type="button"
                  onClick={() => setShowBulkPicker(true)}
                  disabled={submitting}
                  className="w-full px-4 py-2.5 text-sm font-medium text-gray-900 transition-all bg-white border border-gray-900 shadow-sm rounded-xl hover:bg-gray-100 focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Pilih Banyak
                </button>
              </div>
            </div>

            {/* Mount kondisional: hook fetch di modal hanya hidup saat terbuka. */}
            {showBulkPicker && (
              <AdjustmentProductPickerModal
                onClose={() => setShowBulkPicker(false)}
                onAdd={addAdjustmentItemsBulk}
                outletId={outletId}
                existingIds={adjustmentItems.map((item) => item.product_unit_id)}
                uomLabel={adjUom}
                uomFactor={adjFactor}
              />
            )}

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left whitespace-nowrap">
                <thead className="text-xs font-semibold tracking-wider text-gray-500 uppercase bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 w-10">#</th>
                    {/* 240726 (#5): klik header utk urut abjad asc/desc/off; tersimpan. */}
                    <th className="px-4 py-3 min-w-[260px]">
                      <button type="button" onClick={cycleProductSort} className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-700">
                        Produk
                        <span className="text-[10px]">{productSort === "asc" ? "▲" : productSort === "desc" ? "▼" : "⇅"}</span>
                      </button>
                    </th>
                    <th className="px-4 py-3 min-w-[130px]">Stok Sistem{adjFactor > 1 ? ` (${adjUom})` : ""}</th>
                    <th className="px-4 py-3 min-w-[150px]">{inputMode === "DELTA" ? "Qty Koreksi" : "Stok Fisik"}{adjFactor > 1 ? ` (${adjUom})` : ""}</th>
                    <th className="px-4 py-3 min-w-[130px]">Selisih{adjFactor > 1 ? ` (${adjUom})` : ""}</th>
                    <th className="px-4 py-3 min-w-[130px]">Status</th>
                    <th className="px-4 py-3 min-w-[240px]">Catatan</th>
                    <th className="px-4 py-3 min-w-[80px]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayAdjustmentItems.map((item, index) => {
                    const rowStatus = getRowStatus(item, duplicatedIds)
                    const isUnloaded = rowStatus === "unloaded"
                    // 230726: baris yang ditolak backend (stok keburu berkurang saat
                    // submit) disorot merah pekat — prioritas di atas status lain.
                    const isProblem = problemIds.includes(String(item.product_unit_id || "").trim())
                    const rowBg = isProblem ? "bg-red-100" :
                                  rowStatus === "changed" ? "bg-blue-50/30" :
                                  rowStatus === "duplicate" ? "bg-red-50/30" :
                                  rowStatus === "incomplete" ? "bg-amber-50/20" :
                                  ""
                    return (
                      <tr key={item.key} className={`transition-colors hover:bg-brand-50 ${rowBg}`}>
                        <td className="px-4 py-3 text-gray-400 align-middle text-xs">{index + 1}</td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-gray-800 leading-snug">
                              {item.product_unit_name || "-"}
                            </span>
                            {item.product_unit_sku && (
                              <span className="text-xs text-gray-400">{item.product_unit_sku}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center gap-1.5">
                            <div className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-lg min-w-[3.5rem]">
                              {item.is_loading_stock ? (
                                <span className="text-gray-400 animate-pulse">···</span>
                              ) : (item.system_stock === null || item.system_stock === undefined
                                  ? "-"
                                  : pcsToUom(item.system_stock))}
                            </div>
                            {/* Inline reload for UNLOADED rows */}
                            {isUnloaded && !item.is_loading_stock && (
                              <button
                                type="button"
                                onClick={() => reloadStockForRow(item.key)}
                                className="inline-flex items-center justify-center w-7 h-7 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                                title="Muat ulang stok sistem"
                              >
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          {item.input_mode === "DELTA" ? (
                            <div className="flex items-center gap-1.5">
                              {deltaDirection === "BOTH" ? (
                                <select
                                  value={item.delta_direction === "OUT" ? "OUT" : "IN"}
                                  onChange={(e) =>
                                    updateAdjustmentItem(item.key, "delta_direction", e.target.value)
                                  }
                                  className={`px-2 py-2 text-xs font-semibold rounded-lg border whitespace-nowrap focus:outline-none ${
                                    item.delta_direction === "OUT"
                                      ? "bg-red-50 text-red-700 border-red-200"
                                      : "bg-green-50 text-green-700 border-green-200"
                                  }`}
                                  title="Arah koreksi baris ini (mode 2 arah)"
                                >
                                  <option value="IN">Masuk</option>
                                  <option value="OUT">Keluar</option>
                                </select>
                              ) : (
                              <span
                                className={`px-2 py-2 text-xs font-semibold rounded-lg border whitespace-nowrap ${
                                  deltaDirection === "OUT"
                                    ? "bg-red-50 text-red-700 border-red-200"
                                    : "bg-green-50 text-green-700 border-green-200"
                                }`}
                                title="Arah koreksi diatur global di atas (Arah Koreksi)"
                              >
                                {deltaDirection === "OUT" ? "Keluar" : "Masuk"}
                              </span>
                              )}
                              <Input
                                type="number"
                                min="0"
                                step="any"
                                id={`qty-adj-input-${index}`}
                                value={
                                  qtyDraft[draftKey(item.key, "delta_qty")] ??
                                  pcsToUom(item.delta_qty)
                                }
                                onChange={(e) => {
                                  const raw = e.target.value
                                  setDraft(item.key, "delta_qty", raw)
                                  updateAdjustmentItem(item.key, "delta_qty", uomToPcs(raw))
                                }}
                                onBlur={() => clearDraft(item.key, "delta_qty")}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); focusNextQtyInput() } }}
                                placeholder="0"
                                className="w-full text-center"
                              />
                            </div>
                          ) : (
                            <Input
                              type="number"
                              min="0"
                              step="any"
                              id={`qty-adj-input-${index}`}
                              value={
                                qtyDraft[draftKey(item.key, "physical_stock")] ??
                                pcsToUom(item.physical_stock)
                              }
                              onChange={(e) => {
                                const raw = e.target.value
                                setDraft(item.key, "physical_stock", raw)
                                updateAdjustmentItem(item.key, "physical_stock", uomToPcs(raw))
                              }}
                              onBlur={() => clearDraft(item.key, "physical_stock")}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); focusNextQtyInput() } }}
                              placeholder="0"
                              className="w-full text-center"
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className={`inline-flex items-center justify-center px-3 py-1.5 text-sm font-bold rounded-lg border min-w-[4.5rem] ${
                            item.difference_qty === null
                              ? "bg-gray-50 border-gray-200 text-gray-400"
                              : Number(item.difference_qty) > 0
                              ? "bg-green-50 border-green-200 text-green-700"
                              : Number(item.difference_qty) < 0
                              ? "bg-red-50 border-red-200 text-red-700"
                              : "bg-gray-50 border-gray-200 text-gray-600"
                          }`}>
                            {item.difference_qty === null ? "-"
                              : Number(item.difference_qty) > 0 ? `+${pcsToUom(item.difference_qty)}`
                              : pcsToUom(item.difference_qty)}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <StatusBadge status={rowStatus} />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          {/* 240726 (permintaan user): catatan per produk TIDAK wajib —
                              penanda wajib (bintang header, border merah, teks "wajib") dilepas. */}
                          <Input
                            type="text"
                            value={item.notes}
                            onChange={(e) => updateAdjustmentItem(item.key, "notes", e.target.value)}
                            placeholder="Catatan..."
                            className="w-full"
                          />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <button
                            type="button"
                            onClick={() => removeAdjustmentItem(item.key)}
                            // 270726 (permintaan user): baris TERAKHIR pun boleh dihapus —
                            // dulu tombol mati saat tersisa 1 item sehingga produk salah
                            // pilih tak bisa dibuang tanpa menambah produk lain dulu.
                            // Tabel sudah punya empty state, dan simpan draft/submit tetap
                            // dijaga ("Tambahkan minimal 1 produk untuk draft").
                            disabled={submitting}
                            className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                            title="Hapus baris"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                  {!adjustmentItems.length && (
                    <tr>
                      <td colSpan={8} className="px-5 py-12 text-center">
                        <div className="flex flex-col items-center gap-2 text-gray-400">
                          <svg className="w-8 h-8 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/>
                            <path d="M16 3H8a2 2 0 00-2 2v2h12V5a2 2 0 00-2-2z"/>
                          </svg>
                          <p className="text-sm">Cari produk di atas, lalu klik <strong className="text-gray-600">+ Tambah Item</strong></p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── Footer / submit bar ── */}
      <div className="flex flex-col items-center justify-between gap-3 pt-2 sm:flex-row">
        <SubmitBlocker
          outletId={outletId}
          changedRows={changedRows}
          hasInvalidRows={hasInvalidRows}
          duplicatedIds={duplicatedIds}
          notes={adjustmentNotes}
        />
        <div className="flex items-center w-full gap-3 sm:w-auto">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={!canSaveDraft}
            className="w-full px-5 py-2.5 text-sm font-medium text-gray-800 transition-colors bg-amber-100 border border-amber-300 rounded-xl hover:bg-amber-200 focus:ring-2 focus:ring-amber-200 sm:w-auto disabled:opacity-60"
          >
            Simpan Draft
          </button>
          {adjustmentId && currentAdjustmentStatus === "DRAFT" && (
            <button
              type="button"
              onClick={handleCancelDraft}
              disabled={submitting || loadingSystemStock}
              className="w-full px-5 py-2.5 text-sm font-medium text-red-700 transition-colors bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 focus:ring-2 focus:ring-red-200 sm:w-auto disabled:opacity-60"
            >
              Batalkan Draft
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={submitting || loadingSystemStock}
            className="w-full px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 sm:w-auto disabled:opacity-60"
          >
            Kembali
          </button>
          <button
            type="submit"
            disabled={!canSubmitForm}
            className="w-full px-6 py-2.5 text-sm font-medium text-white transition-all bg-gray-900 shadow-sm rounded-xl hover:bg-gray-800 focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 sm:w-auto disabled:opacity-60"
          >
            {submitting ? "Memproses..." : `Submit Adjustment${changedRows.length ? ` (${changedRows.length})` : ""}`}
          </button>
        </div>
      </div>
    </form>
  )
}

// ─── List page (unchanged except modal wiring) ────────────────────────────────

export default function List() {
  const { can, isSuper, currentBranch, canFilterAllOutlets } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const searchInput = useRef(null)

  const isBranchLocked = !canFilterAllOutlets() && !!currentBranch?.id // ATALLA-403: pemegang permission filter-entity bebas pilih outlet entitasnya

  const page          = searchParams.get("page")
  const search        = searchParams.get("search")
  const productIdsParam = searchParams.get("product_unit_ids")
  // Nilai filter outlet: sentinel grup (GRP_ALL/GRP_HO) atau id outlet tunggal.
  // Default "ikut outlet user"; branch-locked dipaksa ke outlet sendiri.
  const rawOutletFilter = searchParams.get("outlet_ids") ?? getDefaultOutletFilterValue()
  const outletFilter = isBranchLocked ? String(currentBranch.id) : rawOutletFilter
  // Grup/outlet → CSV id utk kontrak endpoint outlet_ids. Kosong => semua outlet.
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletFilter)
  const outlet = resolvedOutletCsv || undefined
  const referenceType = searchParams.get("reference_type")
  const brandIdParam  = searchParams.get("brand_id")
  const modelIdParam  = searchParams.get("product_model_id")
  const dateFrom      = searchParams.get("date_from")
  const dateTo        = searchParams.get("date_to")
  const orderBy       = searchParams.get("order_by")
  const direction     = searchParams.get("direction")
  const effectiveOrderBy = orderBy || "transaction_date"
  const effectiveDirection = direction || "desc"
  // Fetch for any selection, including "ALL" (which sends no outlet_id => all outlets).
  const shouldFetchList = true
  const selectedProductIds = useMemo(
    () => (productIdsParam ? productIdsParam.split(",").map((id) => id.trim()).filter(Boolean) : []),
    [productIdsParam]
  )
  const [inlineProductSearch, setInlineProductSearch] = useState("")
  const [inlineSelectedProductId, setInlineSelectedProductId] = useState("")
  const [selectedProductMetaById, setSelectedProductMetaById] = useState({})

  const [searchText, setSearchText] = useState(search ?? "")

  const productParams = useMemo(
    () => ({
      paginate: true,
      search: inlineProductSearch || undefined,
    }),
    [inlineProductSearch]
  )
  const stockParams = useMemo(
    () => ({
      page,
      search,
      product_unit_ids: productIdsParam,
      outlet_ids: outlet,
      // ATALLA-296: filter by brand & model (backend resolve ke product_unit_id ter-index)
      brand_id: brandIdParam || undefined,
      product_model_id: modelIdParam || undefined,
      reference_type: referenceType,
      date_from: dateFrom,
      date_to: dateTo,
      order_by: effectiveOrderBy,
      direction: effectiveDirection,
      enabled: shouldFetchList,
    }),
    [
      page,
      search,
      productIdsParam,
      outlet,
      brandIdParam,
      modelIdParam,
      referenceType,
      dateFrom,
      dateTo,
      effectiveOrderBy,
      effectiveDirection,
      shouldFetchList,
    ]
  )

  const {
    data: productUnitSelectionRaw,
    isLoading: isLoadingProductUnitSelection,
  } = useProductUnitRepositories(productParams)

  const productUnitOptions = useMemo(() => {
    if (Array.isArray(productUnitSelectionRaw)) return productUnitSelectionRaw
    if (Array.isArray(productUnitSelectionRaw?.data)) return productUnitSelectionRaw.data
    return []
  }, [productUnitSelectionRaw])

  // ATALLA-296: pilihan Brand & Model utk filter pergerakan stok.
  const [modelSearch, setModelSearch] = useState("")
  const { data: brandSelectionRaw, isLoading: isLoadingBrandSelection } =
    useBrandRepositories({ paginate: false })
  const brandOptions = useMemo(() => {
    const rows = Array.isArray(brandSelectionRaw) ? brandSelectionRaw : (brandSelectionRaw?.data ?? [])
    return [{ id: "", name: "Semua Brand" }, ...rows]
  }, [brandSelectionRaw])

  const { data: modelSelectionRaw, isLoading: isLoadingModelSelection } =
    useProductModelRepositories({ paginate: true, search: modelSearch || undefined })
  const modelOptions = useMemo(() => {
    const rows = Array.isArray(modelSelectionRaw) ? modelSelectionRaw : (modelSelectionRaw?.data ?? [])
    return rows
  }, [modelSelectionRaw])

  const { data: stockRaw, isLoading } = useStockTransactionRepositories(stockParams)

  const stockData = useMemo(() => stockRaw ?? {}, [stockRaw])
  const stockRows = useMemo(() => stockData?.data ?? [], [stockData])

  useEffect(() => {
    if (!productUnitOptions.length && !stockRows.length) {
      return
    }

    setSelectedProductMetaById((prev) => {
      const next = { ...prev }

      productUnitOptions.forEach((row) => {
        const id = String(row?.id ?? "")
        if (!id) return
        next[id] = row
      })

      stockRows.forEach((row) => {
        const id = String(row?.product_unit_id ?? "")
        if (!id || next[id]) return

        next[id] = {
          id,
          long_name:
            row?.product_unit?.long_name ||
            row?.product_unit_name ||
            row?.product_unit?.name ||
            row?.product_unit ||
            id,
          name:
            row?.product_unit?.name ||
            row?.product_unit_name ||
            row?.product_unit ||
            id,
          sku: row?.product_unit?.sku || row?.sku || "",
        }
      })

      return next
    })
  }, [productUnitOptions, stockRows])

  const selectedInlineProducts = useMemo(
    () =>
      selectedProductIds.map((id) => {
        const resolved = selectedProductMetaById[String(id)]
        return resolved || { id, long_name: id, name: id, sku: id }
      }),
    [selectedProductIds, selectedProductMetaById]
  )

  useEffect(() => { isLoading ? nProgress.start() : nProgress.done() }, [isLoading])
  useEffect(() => { searchInput.current?.focus() }, [])
  useEffect(() => { setSearchText(search ?? "") }, [search])

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    const merged = { ...current, ...next }
    Object.keys(merged).forEach((key) => {
      if (merged[key] === null || merged[key] === undefined || merged[key] === "") delete merged[key]
    })

    const currentSerialized = new URLSearchParams(current).toString()
    const nextSerialized = new URLSearchParams(merged).toString()

    if (currentSerialized === nextSerialized) {
      return
    }

    setSearchParams(merged)
  }

  const commitSearch = (value) => {
    mergeParams({ search: value || null, page: 1 })
  }

  const updateSearch = (value) => {
    setSearchText(value)
  }

  const clearSearch = () => { setSearchText(""); commitSearch(""); searchInput.current?.focus() }

  const toggleSort = (value) => {
    if ((effectiveDirection === "desc") || effectiveOrderBy !== value) {
      mergeParams({ order_by: value, direction: "asc", page: 1 })
    } else {
      mergeParams({ order_by: value, direction: "desc", page: 1 })
    }
  }

  const dataListHeaders = [
    { isAction: false, content: "Referensi",        sortKey: "reference_id"    },
    { isAction: false, content: "Produk",           sortKey: "product_unit_id" },
    { isAction: false, content: "Outlet",           sortKey: "outlet_id"       },
    { isAction: false, content: "Tipe",             sortKey: "reference_type"  },
    { isAction: false, content: "Qty",              sortKey: "quantity"        },
    { isAction: false, content: "Saldo",            sortKey: "current_stock"   },
    { isAction: false, content: "Tanggal Transaksi", sortKey: "transaction_date"},
    { isAction: false, content: "Dibuat Oleh",      sortKey: "created_by"      },
  ]

  const dataListColumns = [
    {
      type: "default",
      bold: true,
      content: (v) => (
        <div className="flex flex-col gap-1">
          <span>{v.reference_id || "-"}</span>
          {Number(v._grouped_count ?? 1) > 1 && (
            <span className="text-[11px] font-medium text-blue-600">
              FIFO split tergabung: {v._grouped_count} baris
            </span>
          )}
        </div>
      ),
    },
    {
      type: "default",
      content: (v) =>
        v.product_unit?.long_name ||
        v.product_unit_name ||
        v.product_unit?.name ||
        v.product_unit ||
        v.product_unit_id ||
        "-",
    },
    { type: "default", content: (v) => v.outlet?.name || "-" },
    { type: "default", content: (v) => (
        <span className="px-2 py-1 text-xs rounded bg-neutral-100">{v.reference_type}</span>
      ),
    },
    { type: "default", content: (v) => {
        const qty = Number(v.quantity ?? 0)
        return <span className={qty >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>{qty >= 0 ? `+${qty}` : qty}</span>
      },
    },
    { type: "default", content: (v) => <span className="font-semibold text-neutral-700">{v.balance ?? v.current_stock ?? "-"}</span> },
    { type: "default", content: (v) => v.transaction_date ? moment(v.transaction_date).format("YYYY-MM-DD HH:mm") : "-" },
    { type: "default", content: (v) => v.created_by_name || v.created_by || "-" },
  ]

  const updateFilter = (value) => mergeParams({ ...value, page: 1 })
  const removeFilter = () => mergeParams({
    reference_type: null,
    date_from: null,
    date_to: null,
    page: 1,
  })
  const addInlineProductFilter = () => {
    if (!inlineSelectedProductId) {
      toast.error("Pilih product terlebih dahulu")
      return
    }

    const nextIds = Array.from(new Set([...selectedProductIds, String(inlineSelectedProductId)]))
    mergeParams({
      product_unit_ids: nextIds.join(","),
      product_unit_id: null,
      page: 1,
    })
    setInlineSelectedProductId("")
    setInlineProductSearch("")
  }

  const removeInlineProductFilter = (productId) => {
    const nextIds = selectedProductIds.filter((id) => String(id) !== String(productId))
    mergeParams({
      product_unit_ids: nextIds.length ? nextIds.join(",") : null,
      page: 1,
    })
  }

  const handleOutletInlineChange = (value) => {
    if (isBranchLocked) return
    // value = sentinel grup / id outlet tunggal, disimpan apa adanya di URL.
    mergeParams({ outlet_ids: value || null, page: 1 })
  }

  const handleExport = () => {
    const params = new URLSearchParams(searchParams)
    // Force the resolved outlets (locked branch, or drop it for all outlets).
    params.delete("outlet_id")
    if (outlet) {
      params.set("outlet_ids", String(outlet))
    } else {
      params.delete("outlet_ids")
    }
    params.set("report_type", "stock_movement")
    window.open(`${process.env.REACT_APP_INVENTORY_API_URL}/stock/transfer/export?${params.toString()}`, "_blank")
  }

  return (
    <>
      <div className="space-y-6">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl space-y-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[240px_minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-end">
            <div>
              <Label value="Outlet" className="mb-1.5 block text-xs font-medium text-gray-500 uppercase tracking-wide" />
              <OutletFilterSelect
                value={outletFilter}
                onChange={handleOutletInlineChange}
                disabled={isBranchLocked}
                className="block w-full px-2 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-50 disabled:opacity-75"
                title="Outlet"
              />
            </div>

            <div>
              <Label value="Filter Produk" className="mb-1.5 block text-xs font-medium text-gray-500 uppercase tracking-wide" />
              <SelectSearchable
                selection={productUnitOptions}
                isLoading={isLoadingProductUnitSelection}
                value={inlineSelectedProductId}
                onChange={(value) => setInlineSelectedProductId(String(value || ""))}
                keyValue={(v) => v.id}
                title={(v) => v.long_name || v.name}
                description={(v) => [v.sku, v.product_model?.name].filter(Boolean).join(" · ")}
                placeholder="Cari product unit / SKU..."
                onSearchChange={(value) => setInlineProductSearch(value)}
                serverSearch
                clearOnClose={false}
              />
            </div>

            <button
              type="button"
              onClick={addInlineProductFilter}
              disabled={!inlineSelectedProductId}
              className="px-4 py-3 text-sm font-medium text-white transition bg-brand-800 rounded-xl disabled:opacity-60"
            >
              Tambah Produk
            </button>

            <div className="relative">
              <Label value="Cari Referensi" className="mb-1.5 block text-xs font-medium text-gray-500 uppercase tracking-wide" />
              <input
                ref={searchInput}
                onChange={(e) => updateSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commitSearch(searchText) }}
                value={searchText}
                type="text"
                placeholder="Cari referensi..."
                className="w-full py-3 pl-4 pr-10 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
              />
              {!!searchText && (
                <button type="button" onClick={clearSearch}
                  className="absolute inset-y-0 right-3 top-6 flex items-center text-neutral-400 hover:text-neutral-700">✕</button>
              )}
            </div>
          </div>

          {!!selectedInlineProducts.length && (
            <div className="flex flex-wrap items-center gap-2">
              {selectedInlineProducts.map((productItem) => (
                <button
                  key={productItem.id}
                  type="button"
                  onClick={() => removeInlineProductFilter(productItem.id)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full"
                >
                  <span>{productItem.long_name || productItem.name || productItem.id}</span>
                  <span>✕</span>
                </button>
              ))}
            </div>
          )}

          {/* ATALLA-296: filter Brand & Model */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[240px_minmax(0,1fr)]">
            <div>
              <Label value="Brand" className="mb-1.5 block text-xs font-medium text-gray-500 uppercase tracking-wide" />
              <SelectDescription
                selection={brandOptions}
                isLoading={isLoadingBrandSelection}
                value={brandIdParam || ""}
                onChange={(value) => mergeParams({ brand_id: value || null, page: 1 })}
                keyValue={(v) => v.id}
                title={(v) => v.name}
                description={(v) => v.id || ""}
                placeholder="Semua Brand"
              />
            </div>
            <div>
              <Label value="Model" className="mb-1.5 block text-xs font-medium text-gray-500 uppercase tracking-wide" />
              <SelectSearchable
                selection={modelOptions}
                isLoading={isLoadingModelSelection}
                value={modelIdParam || ""}
                serverSearch
                searchValue={modelSearch}
                onSearchChange={(q) => setModelSearch(q)}
                onChange={(value) => mergeParams({ product_model_id: value || null, page: 1 })}
                keyValue={(v) => v.id}
                title={(v) => v.name || v.long_name || v.id}
                description={(v) => v.id}
                placeholder="Semua Model (ketik utk cari)"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Filter
              onSubmit={updateFilter}
              onRemove={removeFilter}
              data={Object.fromEntries(
                Object.entries({
                  reference_type: referenceType,
                  date_from: dateFrom,
                  date_to: dateTo,
                }).filter(([, v]) => v != null)
              )}
            />
            {/* {can("stock_transaction.export") && ( */}
              <button onClick={handleExport}
                className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-green-600 rounded-xl active:hover:scale-95">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none">
                  <path d="M12 3v12"/><path d="M7 10l5 5l5 -5"/><path d="M5 21h14"/>
                </svg>
                <span>Export</span>
              </button>
            {/* )} */}
          </div>
        </div>
        <>
          <Table
            isLoading={isLoading}
            data={stockRows}
            headers={dataListHeaders}
            columns={dataListColumns}
            key={(v) => `${v.reference_id}-${v.product_unit_id}-${v.transaction_date}-${v._grouped_count ?? 1}`}
            isEmpty={!stockRows.length && !search}
            isEmptyOnSearching={!stockRows.length && !!search}
            toggleSort={toggleSort}
            orderBy={effectiveOrderBy}
            direction={effectiveDirection}
          />
          <Pagination links={stockData?.links} from={stockData?.from} to={stockData?.to} total={stockData?.total} />
        </>
      </div>

    </>
  )
}
